<template>
  <div class="viq-article-detail">
    <div class="viq-article-detail__meta">
      <span
        v-if="article.source_type"
        class="viq-article-detail__badge"
        :class="sourceTypeClass"
      >{{ formatSourceType(article.source_type) }}</span>
      <span v-if="article.type" class="viq-article-detail__badge viq-article-detail__badge--type">
        {{ article.type }}
      </span>
      <span
        v-if="article.classification"
        class="viq-article-detail__badge"
        :class="classificationClass"
      >{{ article.classification }}</span>
    </div>

    <h2 class="viq-article-detail__title">{{ article.title }}</h2>

    <div class="viq-article-detail__info">
      <div v-if="article.jurisdiction" class="viq-article-detail__info-row">
        <span class="viq-article-detail__info-label">Jurisdiction</span>
        <span class="viq-article-detail__info-value">{{ article.jurisdiction }}</span>
      </div>
      <div v-if="article.vertical" class="viq-article-detail__info-row">
        <span class="viq-article-detail__info-label">Vertical</span>
        <span class="viq-article-detail__info-value">{{ article.vertical }}</span>
      </div>
      <div v-if="article.published_date" class="viq-article-detail__info-row">
        <span class="viq-article-detail__info-label">Published</span>
        <span class="viq-article-detail__info-value">{{ formatDate(article.published_date) }}</span>
      </div>
      <div v-if="article.last_updated" class="viq-article-detail__info-row">
        <span class="viq-article-detail__info-label">Updated</span>
        <span class="viq-article-detail__info-value">{{ formatDate(article.last_updated) }}</span>
      </div>
    </div>

    <div v-if="article.content" class="viq-article-detail__content" v-html="sanitizedContent" />
    <template v-else-if="displayExcerpts.length">
      <div class="viq-article-detail__excerpt-section">
        <div class="viq-article-detail__excerpt-label">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M3 4h8M3 7h6M3 10h7" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
          </svg>
          {{ displayExcerpts.length === 1 ? 'Cited passage' : 'Cited passages' }}
        </div>
        <blockquote
          v-for="(exc, idx) in displayExcerpts"
          :key="idx"
          class="viq-article-detail__excerpt"
        >{{ exc }}</blockquote>
      </div>
      <a
        v-if="article.url"
        :href="localUrl(article.url)"
        target="_blank"
        rel="noopener noreferrer"
        class="viq-article-detail__load-full"
      >
        View full article
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M5 1h6v6M11 1L5 7" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </a>
    </template>

    <div v-if="!article.content && !article.excerpt" class="viq-article-detail__empty">
      <p>No additional content available for this source.</p>
      <p v-if="article.url" class="viq-article-detail__empty-hint">
        Click "View on Platform" above to see the full source.
      </p>
    </div>

    <ViqRelatedContent
      v-if="article.related && article.related.length > 0"
      :items="article.related"
    />
  </div>
</template>

<script>
import DOMPurify from "dompurify";

export default {
  name: "ViqArticleDetail",
  components: {
    ViqRelatedContent: () => import("./ViqRelatedContent"),
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
  },
  computed: {
    sanitizedContent() {
      if (!this.article.content) return "";
      return DOMPurify.sanitize(this.article.content);
    },
    sourceTypeClass() {
      const map = {
        analyst: "viq-article-detail__badge--analyst",
        ai_generated: "viq-article-detail__badge--ai",
        legislation: "viq-article-detail__badge--legislation",
      };
      return map[this.article.source_type] || "";
    },
    displayExcerpts() {
      if (this.article.excerpts && this.article.excerpts.length) {
        return this.article.excerpts;
      }
      if (this.article.excerpt) {
        return [this.article.excerpt];
      }
      return [];
    },
    showSummary() {
      const summary = (this.article.content_summary || "").trim();
      const excerpt = (this.article.excerpt || "").trim();
      return summary && summary !== excerpt && !excerpt.startsWith(summary);
    },
    classificationClass() {
      const map = {
        Actionable: "viq-article-detail__badge--actionable",
        Indicative: "viq-article-detail__badge--indicative",
        Informative: "viq-article-detail__badge--informative",
      };
      return map[this.article.classification] || "";
    },
  },
  methods: {
    localUrl(url) {
      if (!url) return "/";
      try {
        const parsed = new URL(url);
        return parsed.pathname + parsed.search + parsed.hash;
      } catch (e) {
        return url;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    },
    formatSourceType(type) {
      const map = {
        analyst: "Analyst",
        ai_generated: "AI Generated",
        legislation: "Legislation",
      };
      return map[type] || type || "";
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-article-detail {
  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: $viq-space-xs;
    margin-bottom: $viq-space-md;
  }

  &__badge {
    display: inline-flex;
    padding: 2px 8px;
    border-radius: $viq-radius-sm;
    font-size: $viq-font-xs;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    background: #f1f5f9;
    color: #475569;

    &--analyst {
      background: #dbeafe;
      color: #1d4ed8;
    }
    &--ai {
      background: #ede9fe;
      color: #6d28d9;
    }
    &--legislation {
      background: #f1f5f9;
      color: #475569;
    }
    &--type {
      background: #f3f4f6;
      color: #6b7280;
    }
    &--actionable {
      background: #fee2e2;
      color: #b91c1c;
    }
    &--indicative {
      background: #fef3c7;
      color: #92400e;
    }
    &--informative {
      background: #e0f2fe;
      color: #0369a1;
    }
  }

  &__title {
    font-size: $viq-font-lg;
    font-weight: 700;
    color: $viq-text-primary;
    margin: 0 0 $viq-space-md;
    line-height: 1.3;
  }

  &__info {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: $viq-space-sm $viq-space-lg;
    margin-bottom: $viq-space-lg;
    padding: $viq-space-md;
    background: $viq-bg-secondary;
    border-radius: $viq-radius-md;
  }

  &__info-row {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__info-label {
    font-size: $viq-font-xs;
    font-weight: 600;
    color: $viq-text-muted;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__info-value {
    font-size: $viq-font-sm;
    color: $viq-text-primary;

    &--cap {
      text-transform: capitalize;
    }
  }

  &__platform-link {
    margin-bottom: $viq-space-lg;

    a {
      display: inline-flex;
      align-items: center;
      gap: $viq-space-xs;
      padding: $viq-space-sm $viq-space-md;
      background: $viq-accent;
      color: #fff;
      font-size: $viq-font-sm;
      font-weight: 600;
      text-decoration: none;
      border-radius: $viq-radius-md;
      transition: background 0.15s;

      &:hover {
        background: $viq-accent-hover;
      }
    }
  }

  &__content {
    font-size: $viq-font-base;
    line-height: 1.7;
    color: $viq-text-primary;
    border-top: 1px solid $viq-border;
    padding-top: $viq-space-lg;

    ::v-deep {
      h1, h2, h3, h4, h5, h6 {
        margin: $viq-space-lg 0 $viq-space-sm;
        font-weight: 700;
      }
      p {
        margin: 0 0 $viq-space-md;
      }
      ul, ol {
        padding-left: $viq-space-lg;
        margin: $viq-space-sm 0 $viq-space-md;
      }
      ul {
        list-style-type: disc;

        ul {
          list-style-type: circle;
          margin-top: $viq-space-xs;
          margin-bottom: 0;

          ul {
            list-style-type: square;
          }
        }
      }
      ol {
        list-style-type: decimal;

        ol {
          list-style-type: lower-alpha;
          margin-top: $viq-space-xs;
          margin-bottom: 0;

          ol {
            list-style-type: lower-roman;
          }
        }
      }
      li {
        margin-bottom: $viq-space-xs;
        line-height: 1.6;

        > ul, > ol {
          margin-top: $viq-space-xs;
        }
      }
      a {
        color: $viq-accent;
        text-decoration: underline;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin: $viq-space-md 0;
        font-size: $viq-font-sm;
        overflow-x: auto;
        display: block;

        thead {
          background: $viq-bg-secondary;
        }
        th, td {
          padding: $viq-space-sm $viq-space-md;
          border: 1px solid $viq-border;
          text-align: left;
          vertical-align: top;
        }
        th {
          background: $viq-bg-secondary;
          font-weight: 600;
          white-space: nowrap;
        }
        tbody tr:nth-child(even) {
          background: rgba($viq-bg-secondary, 0.5);
        }
        tbody tr:hover {
          background: rgba($viq-bg-secondary, 0.8);
        }
      }
    }
  }

  &__excerpt-section {
    border-top: 1px solid $viq-border;
    padding-top: $viq-space-lg;
  }

  &__excerpt-label {
    display: flex;
    align-items: center;
    gap: $viq-space-xs;
    font-size: $viq-font-xs;
    font-weight: 600;
    color: $viq-text-muted;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: $viq-space-sm;
  }

  &__excerpt {
    margin: 0;
    padding: $viq-space-md $viq-space-lg;
    border-left: 3px solid $viq-accent;
    background: $viq-citation-bg;
    border-radius: 0 $viq-radius-md $viq-radius-md 0;
    font-size: $viq-font-base;
    line-height: 1.7;
    color: $viq-text-primary;
    font-style: italic;

    & + & {
      margin-top: $viq-space-sm;
    }
  }

  &__summary {
    margin: $viq-space-md 0 0;
    font-size: $viq-font-sm;
    line-height: 1.6;
    color: $viq-text-secondary;
  }

  &__load-full {
    display: inline-flex;
    align-items: center;
    gap: $viq-space-xs;
    margin-top: $viq-space-md;
    padding: $viq-space-sm $viq-space-md;
    background: $viq-accent;
    color: #fff;
    border: none;
    font-size: $viq-font-sm;
    font-weight: 600;
    text-decoration: none;
    border-radius: $viq-radius-md;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
      background: $viq-accent-hover;
    }
  }

  &__empty {
    text-align: center;
    padding: $viq-space-lg 0;
    color: $viq-text-secondary;
    font-size: $viq-font-sm;

    p {
      margin: 0 0 $viq-space-xs;
    }
  }

  &__empty-hint {
    font-size: $viq-font-xs;
    color: $viq-text-muted;
  }
}
</style>
